import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Diagnostics } from "../../../../../../services";
import Panel, {
	EmptyState,
	PanelHint,
	PanelTitle,
} from "../../components/Panel";
import DataTable, { Accent, TableScroll } from "../../components/DataTable";
import MemoryChart from "../../components/MemoryChart";
import CopyReportButton from "../../components/CopyReportButton";
import ServiceOperationMark from "../../components/ServiceOperationMark";
import useRequest from "../../hooks/useRequest";
import { buildIncidentReport } from "../../utils/buildReport";
import { isServiceOperation } from "../../utils/isServiceOperation";
import {
	formatBytes,
	formatDateTime,
	formatDuration,
	formatNumber,
	formatPercent,
} from "../../utils/format";

const Root = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;

	height: 100%;
	overflow: auto;
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
`;

const HeaderText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const Summary = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
`;

const Metric = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;

	font-family: Lato;
	font-size: 13px;
`;

const MetricValue = styled.div`
	font-size: 18px;
	font-weight: 600;
`;

const IncidentDetails: React.FC<IncidentDetails.Props> = ({ id }) => {
	const { t } = useTranslation();

	const { data, loading } = useRequest<Diagnostics.IncidentResponse>(
		() => Diagnostics.getIncident(id),
		[id],
	);

	const buildReport = useCallback(async () => {
		const response = data ?? (await Diagnostics.getIncident(id));
		if (!response) {
			throw new Error("Incident details are not available");
		}
		return buildIncidentReport(response);
	}, [data, id]);

	if (!data) {
		return <EmptyState>{loading ? "…" : "—"}</EmptyState>;
	}

	const { incident, context } = data;

	return (
		<Root>
			<Panel>
				<HeaderRow>
					<HeaderText>
						<PanelTitle>
							{t(
								"settings.systemPerformance.incidents.details.title",
							)}
						</PanelTitle>
						<PanelHint>
							{`${incident.service} · ${formatDateTime(
								incident.ts,
							)} · ${incident.pod} · ${t(
								`settings.systemPerformance.incidents.reasons.${incident.reason}`,
							)}`}
						</PanelHint>
					</HeaderText>
					<CopyReportButton
						labelKey="settings.systemPerformance.filters.copyDetails"
						doneKey="settings.systemPerformance.filters.copyDetailsDone"
						errorKey="settings.systemPerformance.filters.copyReportError"
						build={buildReport}
					/>
				</HeaderRow>

				<Summary>
					<Metric>
						{t(
							"settings.systemPerformance.incidents.details.memory",
						)}
						<MetricValue>
							{formatBytes(incident.memory.heapUsed)}
						</MetricValue>
					</Metric>
					<Metric>
						{t(
							"settings.systemPerformance.incidents.details.baseline",
						)}
						<MetricValue>
							{formatBytes(incident.baseline.heapUsed)}
						</MetricValue>
					</Metric>
					<Metric>
						{t("settings.systemPerformance.overview.rss")}
						<MetricValue>
							{formatBytes(incident.memory.rss)}
						</MetricValue>
					</Metric>
					<Metric>
						{t("settings.systemPerformance.overview.eventLoop")}
						<MetricValue>
							{formatDuration(incident.eventLoopDelayMax)}
						</MetricValue>
					</Metric>
				</Summary>
			</Panel>

			<Panel>
				<PanelTitle>
					{t("settings.systemPerformance.incidents.details.context")}
				</PanelTitle>
				<MemoryChart
					pods={[{ pod: incident.pod, points: context }]}
					calls={[]}
				/>
			</Panel>

			<Panel>
				<PanelTitle>
					{t(
						"settings.systemPerformance.incidents.details.operations",
					)}
				</PanelTitle>

				{incident.operations?.length ? (
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.details.operation",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.calls",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.baselineCalls",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.deviation",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.errors",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.avgDuration",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.operations.map((operation) => (
									<tr key={operation.name}>
										<td className="wide">
											{operation.name}
											<ServiceOperationMark
												name={operation.name}
											/>
										</td>
										<td className="numeric">
											{formatNumber(operation.calls)}
										</td>
										<td className="numeric">
											{operation.deviationPercent === null
												? t(
														"settings.systemPerformance.incidents.details.noBaseline",
												  )
												: formatNumber(
														operation.baselineCalls,
												  )}
										</td>
										<td className="numeric">
											<Accent
												tone={
													!isServiceOperation(
														operation.name,
													) &&
													(operation.deviationPercent ??
														0) > 50
														? "danger"
														: "muted"
												}
											>
												{formatPercent(
													operation.deviationPercent,
												)}
											</Accent>
										</td>
										<td className="numeric">
											<Accent
												tone={
													operation.errors
														? "danger"
														: "muted"
												}
											>
												{formatNumber(operation.errors)}
											</Accent>
										</td>
										<td className="numeric">
											{formatDuration(
												operation.avgDuration,
											)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				) : (
					<EmptyState>
						{t("settings.systemPerformance.overview.empty")}
					</EmptyState>
				)}
			</Panel>

			<Panel>
				<PanelTitle>
					{t(
						"settings.systemPerformance.incidents.details.subscriptions",
					)}
				</PanelTitle>

				{incident.subscriptions?.length ? (
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.details.channel",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.count",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.subscriptions.map((subscription) => (
									<tr key={subscription.name}>
										<td className="wide">
											{subscription.name}
										</td>
										<td className="numeric">
											{formatNumber(subscription.count)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				) : (
					<EmptyState>
						{t("settings.systemPerformance.overview.empty")}
					</EmptyState>
				)}
			</Panel>

			{incident.cpuProfile?.blocks?.length ? (
				<Panel>
					<PanelTitle>
						{t(
							"settings.systemPerformance.incidents.details.cpuBlocks",
						)}
					</PanelTitle>
					<PanelHint>
						{t(
							"settings.systemPerformance.incidents.details.cpuBlocksHint",
						)}
					</PanelHint>

					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.details.cpuStack",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.cpuBlockDuration",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.cpuBlockOffset",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.cpuProfile.blocks.map((entry) => (
									<tr
										key={`${entry.offsetMs}#${entry.frames.join(
											" < ",
										)}`}
									>
										<td className="wide">
											{entry.frames.join(" ← ")}
										</td>
										<td className="numeric">
											{formatDuration(entry.ms)}
										</td>
										<td className="numeric">
											{formatDuration(entry.offsetMs)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				</Panel>
			) : null}

			{incident.cpuProfile?.byFunction?.length ? (
				<Panel>
					<PanelTitle>
						{t(
							"settings.systemPerformance.incidents.details.cpuProfile",
						)}
					</PanelTitle>
					<PanelHint>
						{t(
							"settings.systemPerformance.incidents.details.cpuProfileHint",
							{
								duration: formatDuration(
									incident.cpuProfile.durationMs,
								),
								samples: formatNumber(
									incident.cpuProfile.sampleCount,
								),
								interval: `${formatNumber(
									incident.cpuProfile.samplingIntervalUs,
								)} µs`,
								total: formatDuration(
									incident.cpuProfile.totalMs,
								),
							},
						)}
					</PanelHint>

					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.details.cpuFunction",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.cpuSelf",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.cpuTotal",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.cpuProfile.byFunction.map((entry) => (
									<tr key={`${entry.file}#${entry.name}`}>
										<td className="wide">
											{entry.name}
											{entry.file ? (
												<Accent tone="muted">
													{` · ${entry.file}`}
												</Accent>
											) : null}
										</td>
										<td className="numeric">
											{formatDuration(entry.selfMs)}
										</td>
										<td className="numeric">
											{formatDuration(entry.totalMs)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				</Panel>
			) : null}

			{incident.cpuProfile?.byStack?.length ? (
				<Panel>
					<PanelTitle>
						{t(
							"settings.systemPerformance.incidents.details.cpuStacks",
						)}
					</PanelTitle>
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.details.cpuStack",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.details.cpuSelf",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.cpuProfile.byStack.map((entry) => (
									<tr key={entry.frames.join(" < ")}>
										<td className="wide">
											{entry.frames.join(" ← ")}
										</td>
										<td className="numeric">
											{formatDuration(entry.selfMs)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				</Panel>
			) : null}

			{incident.allocations?.byFunction?.length ? (
				<Panel>
					<PanelTitle>
						{t(
							"settings.systemPerformance.incidents.details.allocations",
						)}
					</PanelTitle>
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.overview.function",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.size",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{incident.allocations.byFunction.map(
									(entry) => (
										<tr key={`${entry.file}#${entry.name}`}>
											<td className="wide">
												{entry.name}
												{entry.file ? (
													<Accent tone="muted">
														{` · ${entry.file}`}
													</Accent>
												) : null}
											</td>
											<td className="numeric">
												{formatBytes(entry.selfSize)}
											</td>
										</tr>
									),
								)}
							</tbody>
						</DataTable>
					</TableScroll>
				</Panel>
			) : null}
		</Root>
	);
};

declare namespace IncidentDetails {
	interface Props {
		id: string;
	}
}

export default IncidentDetails;
